import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableSortLabel,
  TableHead,
  TableContainer,
  LinearProgress,
  TablePagination
} from '@material-ui/core';

import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useProductEntityPrefix,
  useFindProductValidations
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductValidation
} from '../Product.interface';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { getProductEntityName } from '../Product.config';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import { stripHtml } from '../../../common/formatter';

const ValidationsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productValidationList,
    isLoadingProductValidations,
    loadingProductValidationsError,
    findProductValidations
  } = useFindProductValidations(productId);

  const findProductValidationsWithParams = useCallback(() => {
    findProductValidations({ page, limit, order, direction, ...apiParams });
  }, [findProductValidations, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductValidationsWithParams;
    }
    findProductValidationsWithParams();
  }, [fetchRef, findProductValidationsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.Validation
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productValidation.id'}
                  direction={getSortDirection('productValidation.id')}
                  onClick={() => handleSort('productValidation.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productValidation.title'}
                  direction={getSortDirection('productValidation.title')}
                  onClick={() => handleSort('productValidation.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productValidation.description'}
                  direction={getSortDirection('productValidation.description')}
                  onClick={() => handleSort('productValidation.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productValidation.createdBy'}
                  direction={getSortDirection('productValidation.createdBy')}
                  onClick={() => handleSort('productValidation.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductValidations && (
              <TableRow>
                <TableCell colSpan={7} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productValidationList?.data?.map(
              (validation: ProductValidation) => (
                <TableRow
                  hover
                  key={validation.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/validations/${validation.id}`
                    )
                  }
                >
                  <TableCell>{prefix + validation.id}</TableCell>
                  <WrappedTableCell>{validation.title}</WrappedTableCell>
                  <WrappedTableCell>
                    <div className='truncate'>
                      {stripHtml(validation.description || '')}
                    </div>
                  </WrappedTableCell>
                  <TableCell>
                    {`${validation.task.createdBy?.firstName} ${validation.task.createdBy?.lastName}`}
                  </TableCell>
                  <TableCell>
                    {validation.task.user
                      ? `${validation.task.user?.firstName} ${validation.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={validation.task} />
                  </TableCell>
                  <TableCell>
                    {validation.components.length !== 0
                      ? validation.components
                          .map((component: ProductComponent) => component.name)
                          .join(', ')
                      : 'No Components'}
                  </TableCell>
                  <TableCell>
                    {validation.release
                      ? validation.release.name
                      : 'No Release'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productValidationList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductValidationsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('validation')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default ValidationsTable;
